import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getUserPreferences, updateUserPreferences, updatePassword } from '../services/api';
import { useToast } from '../components/Toast';
import './Ajustes.css';

function Ajustes() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preferences, setPreferences] = useState({
    notifications_enabled: true,
    email_notifications: true,
    push_notifications: true,
  });

  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [showPasswordSection, setShowPasswordSection] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    loadPreferences();
  }, []);

  const loadPreferences = async () => {
    try {
      const response = await getUserPreferences();
      if (response.data) {
        setPreferences({ ...preferences, ...response.data });
      }
    } catch (error) {
      console.error('Error al cargar preferencias:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSavePreferences = async () => {
    setSaving(true);
    try {
      await updateUserPreferences(preferences);
      showToast('Preferencias guardadas correctamente', 'success');
    } catch (error) {
      console.error('Error al guardar preferencias:', error);
      showToast('Error al guardar preferencias', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      showToast('Las contraseñas no coinciden', 'error');
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      showToast('La contraseña debe tener al menos 6 caracteres', 'error');
      return;
    }

    setChangingPassword(true);
    try {
      await updatePassword({
        current_password: passwordForm.currentPassword,
        new_password: passwordForm.newPassword
      });
      showToast('Contraseña actualizada correctamente', 'success');
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setShowPasswordSection(false);
    } catch (error) {
      console.error('Error al cambiar contraseña:', error);
      showToast(error.response?.data?.error || 'Error al cambiar contraseña', 'error');
    } finally {
      setChangingPassword(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="ajustes-container">
      <div className="ajustes-header">
        <button className="back-button" onClick={() => navigate(-1)}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="15 18 9 12 15 6"></polyline>
          </svg>
        </button>
        <h1>⚙️ Ajustes</h1>
      </div>

      <div className="ajustes-content">
        {/* Notificaciones */}
        <section className="ajustes-section">
          <div className="section-header">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
              <path d="M13.73 21a2 2 0 0 1-3.46 0" />
            </svg>
            <h2>Notificaciones</h2>
          </div>

          <div className="setting-item">
            <div className="setting-info">
              <label>Activar notificaciones</label>
              <p>Recibe notificaciones sobre actividad en tu comunidad</p>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={preferences.notifications_enabled}
                onChange={(e) => setPreferences({ ...preferences, notifications_enabled: e.target.checked })}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>

          {preferences.notifications_enabled && (
            <>
              <div className="setting-item">
                <div className="setting-info">
                  <label>Notificaciones por email</label>
                  <p>Recibe actualizaciones por correo electrónico</p>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={preferences.email_notifications}
                    onChange={(e) => setPreferences({ ...preferences, email_notifications: e.target.checked })}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>

              <div className="setting-item">
                <div className="setting-info">
                  <label>Notificaciones push</label>
                  <p>Recibe notificaciones instantáneas en tu navegador</p>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={preferences.push_notifications}
                    onChange={(e) => setPreferences({ ...preferences, push_notifications: e.target.checked })}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </>
          )}
        </section>

        {/* Seguridad */}
        <section className="ajustes-section">
          <div className="section-header">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
            <h2>Seguridad</h2>
          </div>

          <div className="setting-item">
            <div className="setting-info">
              <label>Cambiar contraseña</label>
              <p>Actualiza tu contraseña de acceso</p>
            </div>
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => setShowPasswordSection(!showPasswordSection)}
            >
              {showPasswordSection ? 'Cancelar' : 'Cambiar'}
            </button>
          </div>

          {showPasswordSection && (
            <form onSubmit={handlePasswordChange} className="password-form">
              <div className="form-group">
                <label>Contraseña actual</label>
                <input
                  type="password"
                  value={passwordForm.currentPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                  required
                  className="form-control"
                  placeholder="Ingresa tu contraseña actual"
                />
              </div>
              <div className="form-group">
                <label>Nueva contraseña</label>
                <input
                  type="password"
                  value={passwordForm.newPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                  required
                  minLength="6"
                  className="form-control"
                  placeholder="Mínimo 6 caracteres"
                />
              </div>
              <div className="form-group">
                <label>Confirmar nueva contraseña</label>
                <input
                  type="password"
                  value={passwordForm.confirmPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                  required
                  className="form-control"
                  placeholder="Repite la nueva contraseña"
                />
              </div>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={changingPassword}
              >
                {changingPassword ? 'Actualizando...' : 'Actualizar contraseña'}
              </button>
            </form>
          )}
        </section>

        {/* Botón guardar preferencias */}
        <div className="ajustes-actions">
          <button
            className="btn btn-primary btn-lg"
            onClick={handleSavePreferences}
            disabled={saving}
          >
            {saving ? 'Guardando...' : 'Guardar preferencias'}
          </button>
        </div>

        {/* Zona de peligro */}
        <section style={{ marginTop: '32px', padding: '20px', border: '1px solid #fecaca', borderRadius: '12px', background: '#fff5f5' }}>
          <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#b91c1c', marginBottom: '8px' }}>Zona de peligro</h3>
          <p style={{ fontSize: '14px', color: '#7f1d1d', marginBottom: '16px' }}>
            Si deseas eliminar tu cuenta y todos los datos asociados, pulsa el botón. Esta acción es permanente.
          </p>
          <a
            href="/eliminar-cuenta"
            style={{ display: 'inline-block', background: '#dc2626', color: '#fff', textDecoration: 'none', borderRadius: '8px', padding: '10px 20px', fontSize: '14px', fontWeight: '700' }}
          >
            Solicitar eliminación de cuenta
          </a>
        </section>
      </div>
    </div>
  );
}

export default Ajustes;
